<template>
  <div class="feature-toggler">
    <div
      class="feature-toggler__validation"
      :class="{
        'feature-toggler__validation--invalid': hasValidationErrors,
        'feature-toggler__validation--valid': !hasValidationErrors,
      }"
    >
      <span v-if="validationLoading">Validating...</span>
      <span v-else-if="!hasValidationErrors">No validation errors.</span>
      <template v-else>
        <div v-if="flagErrors.length > 0" class="feature-toggler__validation-section">
          <b>Flag validation errors</b>
          <ul>
            <li v-for="error in flagErrors" :key="`flag-${error}`">{{ error }}</li>
          </ul>
        </div>
        <div v-if="orderErrors.length > 0" class="feature-toggler__validation-section">
          <b>Order config validation errors</b>
          <ul>
            <li v-for="error in orderErrors" :key="`order-${error}`">{{ error }}</li>
          </ul>
        </div>
      </template>
    </div>

    <div class="feature-toggler__toggles">
      <div
        v-for="(value, key) in { ...releaseFlags, ...toggleLogic }"
        :key="key"
        class="feature-toggler__toggles--toggle"
      >
        <b>{{ key }}:</b>
        <v-switch
          v-if="isBoolean(value)"
          :input-value="value"
          @change="debouncedUpdateToggle({ key, value: $event })"
        />
        <v-text-field
          v-else-if="isString(value)"
          :value="String(value)"
          @input="debouncedUpdateToggle({ key, value: $event })"
        />
        <v-text-field
          v-else-if="isObject(value) || isNumber(value)"
          :value="JSON.stringify(value)"
          @input="debouncedUpdateToggle({ key, value: $event, parse: true })"
        />
        <span>{{ value }}</span>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { mapState, mapActions } from 'vuex';
import { isBoolean, isString, isNumber, isObject, debounce, has, get } from 'lodash';

export default {
  data() {
    return {
      flagErrors: [],
      orderErrors: [],
      validationLoading: false,
      latestValidationRequestId: 0,
    };
  },
  created() {
    this.validateFlags();
  },
  computed: {
    ...mapState('clientConfig', ['toggleLogic', 'releaseFlags']),
    hasValidationErrors() {
      return this.flagErrors.length > 0 || this.orderErrors.length > 0;
    },
  },
  methods: {
    ...mapActions('clientConfig', ['setToggleLogicValue', 'setReleaseFlagsValue']),
    isBoolean,
    isString,
    isObject,
    isNumber,
    async validateFlags() {
      const requestId = this.latestValidationRequestId + 1;
      this.latestValidationRequestId = requestId;
      this.validationLoading = true;

      try {
        const response = await axios.post('/api/client-config/validate-feature-flags', {
          toggleLogic: this.toggleLogic,
          releaseFlags: this.releaseFlags,
        });

        if (requestId !== this.latestValidationRequestId) {
          return;
        }

        this.flagErrors = response.data.flagErrors;
        this.orderErrors = response.data.orderErrors;
      } catch (error) {
        if (requestId !== this.latestValidationRequestId) {
          return;
        }

        this.flagErrors = [
          get(error, 'response.data.message') ||
            get(error, 'message') ||
            'Unable to validate feature flags at the moment.',
        ];
        this.orderErrors = [];
      } finally {
        if (requestId === this.latestValidationRequestId) {
          this.validationLoading = false;
        }
      }
    },
    debouncedUpdateToggle: debounce(async function({ key: toggle, value, parse = false }) {
      if (parse) {
        try {
          value = JSON.parse(value);
        } catch (error) {
          this.flagErrors = [`${toggle}: invalid JSON value.`];
          this.orderErrors = [];
          this.validationLoading = false;
          return;
        }
      }

      if (has(this.releaseFlags, toggle)) {
        this.setReleaseFlagsValue({ toggle, value });
      } else {
        this.setToggleLogicValue({ toggle, value });
      }

      await this.validateFlags();
    }, 800),
  },
};
</script>

<style scoped lang="scss">
@import '@style/base/_variables.scss';

.feature-toggler {
  height: 100%;
  width: 100%;
  display: flex;
  flex-grow: 1;
  flex-direction: column;
  background-color: white;
  font-size: 1.4rem;

  &__validation {
    margin: 2rem 2rem 1rem 5rem;
    padding: 1rem;
    border: 1px solid #95a5a6;
    background: #f8f9f9;
    max-width: 80%;

    ul {
      margin: 0.6rem 0 0 1.6rem;
    }
  }

  &__validation-section {
    &:not(:first-child) {
      margin-top: 1rem;
    }
  }

  &__validation--invalid {
    border-color: #b71c1c;
    background: #ffebee;
  }

  &__validation--valid {
    border-color: #1b5e20;
    background: #e8f5e9;
  }

  &__toggles {
    max-height: calc(100vh - 35rem);
    overflow-y: auto;
    padding-left: 5rem;
    padding-top: 1rem;
    padding-right: 2rem;

    &--toggle {
      display: flex;
      column-gap: 1rem;
      padding-bottom: 0;
      height: 4rem;
      .v-input--selection-controls {
        padding-top: 0;
        margin-top: 0;
      }
      ::v-deep {
        .v-text-field {
          max-width: 40%;
          padding-top: 0;
          margin-top: 0;
        }
        .v-input__slot {
          min-height: 1.8rem;
        }
        .v-text-field__slot {
          height: 1.8rem;
        }
        .v-text-field__details {
          display: none;
        }
      }
    }
  }
}
</style>
